import {heartbeat, IHeartbeatResponse} from 'core/api/auth'
import {IAction} from 'core/store'
import {delay} from 'redux-saga'
import {all, call, takeLatest} from 'redux-saga/effects'

const LISTEN_HEARTBEAT_REQUEST = 'login/LISTEN_HEARTBEAT_REQUEST'
const EXTEND_TOKEN_REQUEST = 'login/EXTEND_TOKEN_REQUEST'

interface ActionExtendTokenRequest extends IAction<typeof EXTEND_TOKEN_REQUEST> {}
const extendTokenRequest = (): ActionExtendTokenRequest => ({
  type: EXTEND_TOKEN_REQUEST,
})

interface ActionListenHeartbeatRequest extends IAction<typeof LISTEN_HEARTBEAT_REQUEST> {}
const listenHeartbeatRequest = (): ActionListenHeartbeatRequest => ({
  type: LISTEN_HEARTBEAT_REQUEST,
})

export const actions = {
  extendTokenRequest,
  listenHeartbeatRequest,
}

export type SagaDependencies = {
  selectToken: () => void
  withAuth: (fn: (...args: any[]) => any) => void,
  onTokenExpired: () => void,
}

const EXTEND_TOKEN_DEBOUNCE = 10 * 1000 // 10 seconds
function* extendTokenRequestSaga(dependencies: SagaDependencies) {
  // We expect this saga to be called frequently, so we do some debouncing here to prevent the client from making way
  // more HTTP requests than necessary.
  // The best way to implement this is to use `takeLeading`, but this is not yet available in our lib version.
  // https://github.com/redux-saga/redux-saga/tree/master/docs/api#takeleadingpattern-saga-args
  // Instead, with the `takeLatest` implementation here, the saga may debounce forever
  // (e.g. if the saga fires every second, the saga will never get past the delay)!
  // But that is "good enough" in this case since it's very likely that a user taking that many actions will make an
  // API request (which implicitly extends the token lifespan).
  yield delay(EXTEND_TOKEN_DEBOUNCE)
  const token = yield call(dependencies.selectToken)
  if (token) {
    try {
      yield call(dependencies.withAuth, heartbeat, {extend: true})
    } catch {
      // Not terribly important we know about any issue with this request
    }
  }
}

function* heartbeatRequestSaga(dependencies: SagaDependencies) {
  let response: IHeartbeatResponse | undefined
  try {
    response = yield call(dependencies.withAuth, heartbeat, {extend: false})
  } catch {
    // Not terribly important we know about any issue with this request
  }
  return !!response!.data.isAuthenticated
}

const HEARTBEAT_LISTEN_INTERVAL = 60 * 1000 // 1 minute
let isListening = false
/**
 * Periodically checks the expiry of the current auth token.
 */
function* listenHeartbeatRequestSaga(dependencies: SagaDependencies) {
  if (isListening) return
  isListening = true

  while (true) {
    const token = yield call(dependencies.selectToken)
    if (token) {
      const isAlive = yield call(heartbeatRequestSaga, dependencies)
      if (!isAlive) yield call(dependencies.onTokenExpired)
    }
    yield delay(HEARTBEAT_LISTEN_INTERVAL)
  }
}

export function* saga(dependencies: SagaDependencies) {
  yield all([
    takeLatest(LISTEN_HEARTBEAT_REQUEST, listenHeartbeatRequestSaga, dependencies),
    takeLatest(EXTEND_TOKEN_REQUEST, extendTokenRequestSaga, dependencies),
  ])
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./study_reviewer/src/store/login/heartbeat.ts